import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from "../../../components/card/card";
import ImageViewer from "../../../components/ImageViewer";
import { printDate, printLocation } from "../../../utils/helper";
import React, { useState, useEffect } from "react";
import { companyProfile } from "../../../api";
import { parserAPI } from "../../../utils/parser";
import LocationTab from "./LocationTab";
import { useAuth } from "../../../contexts/AuthContext";
import ConfirmationDlg from "../../../components/ConfirmationDlg";
import NormalInput from "../../../components/form/HorizontalNormalInput";
import { toast } from "react-toastify";

function LocationConfiguration() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openLocationLevelSetting, setOpenLocationLevelSetting] =
    useState(false);
  const { config, setConfig } = useAuth();
  const [locationWording, setLocationWording] = useState([]);
  const [locationWordingData, setLocationWordingData] = useState([]);
  const [grouped, setGrouped] = useState([]);

  useEffect(() => {
    if (config) {
      setLocationWording(config.location_wording.split(","));
      setLocationWordingData(config.location_wording.split(","));
    }
  }, [config]);

  const handleChangeLocationWording = (value, index) => {
    console.log(value, index);
    setLocationWordingData((prev) => {
      const newData = [...prev];
      newData[index] = value;
      return newData;
    });
  };

  const isDuplicateLocationWording = (value, currentIndex) => {
    if (!value || value.trim() === "") return false;

    return locationWordingData.some(
      (item, index) =>
        index !== currentIndex &&
        item.toLowerCase().trim() === value.toLowerCase().trim()
    );
  };
  const addMoreLocationWording = () => {
    setLocationWordingData((prev) => {
      const newData = [...prev];
      newData.push("");
      return newData;
    });
  };
  const deleteLocationWording = (index) => {
    if (grouped[index]) {
      toast.error("Unable to delete, there are locations in this level");
      return;
    }
    setLocationWordingData((prev) => {
      const newData = [...prev];
      newData.splice(index, 1);
      return newData;
    });
  };

  const SubmitLocationWording = async () => {
    if (locationWordingData.some((item) => item === "")) {
      toast.error("Please fill all the fields");
      return;
    }

    // Check for duplicate location wording entries
    const duplicates = locationWordingData.filter(
      (item, index) => locationWordingData.indexOf(item) !== index
    );

    if (duplicates.length > 0) {
      toast.error(
        `Duplicate location names found: ("${duplicates[0]}"). Please ensure all location levels have unique names.`
      );
      return;
    }

    // Check for case-insensitive duplicates
    const lowerCaseItems = locationWordingData.map((item) =>
      item.toLowerCase()
    );
    const caseInsensitiveDuplicates = lowerCaseItems.filter(
      (item, index) => lowerCaseItems.indexOf(item) !== index
    );

    if (caseInsensitiveDuplicates.length > 0) {
      const originalDuplicate = locationWordingData.find(
        (item) => item.toLowerCase() === caseInsensitiveDuplicates[0]
      );
      toast.error(
        `Duplicate location names found (case-insensitive): "${originalDuplicate}". Please ensure all location levels have unique names.`
      );
      return;
    }

    try {
      setLoading(true);
      const res = await companyProfile.update(config.id, {
        location_wording: locationWordingData.join(","),
      });
      if (res.ok) {
        const body = await res.json();
        if (body.success) {
          toast.success("Successfully Saved");
          setConfig({
            ...config,
            location_wording: locationWordingData.join(","),
          });
          setOpenLocationLevelSetting(false);
          setSelectedIndex(0);
        }
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onGrouped = (grouped) => {
    setGrouped(grouped);
  };

  const hasAnyDuplicates = () => {
    return locationWordingData.some((item, index) =>
      isDuplicateLocationWording(item, index)
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Setup</a>
            </li>
            <li className="breadcrumb-item active">Location</li>
          </ol>
        </div>
      </div>

      <div className="row gx-4">
        <div className="col-lg-12">
          <Card className="mb-4">
            <CardHeader>
              <div className="d-flex align-items-center">
                <h5 className="flex-1">Location</h5>
                <button
                  className="btn btn-outline-theme ml-auto"
                  onClick={() => setOpenLocationLevelSetting(true)}
                >
                  Update Location Level
                </button>
              </div>

              <ConfirmationDlg
                title="Update Location Level"
                open={openLocationLevelSetting}
                onClose={() => setOpenLocationLevelSetting(false)}
                onSubmit={() => SubmitLocationWording()}
                btnSubmitText="Update"
                btnCancelText="Cancel"
                loading={loading}
                disableSubmit={
                  hasAnyDuplicates() ||
                  locationWordingData.some((item) => item.trim() === "")
                }
              >
                {locationWordingData.map((item, index) => {
                  const isDuplicate = isDuplicateLocationWording(item, index);
                  return (
                    <div key={index}>
                      <div className="d-flex align-items-center">
                        <div className="col-md-10 me-2">
                          <NormalInput
                            full={true}
                            label={"level " + (index + 1)}
                            value={locationWordingData[index]}
                            onchange={(e) =>
                              handleChangeLocationWording(e.target.value, index)
                            }
                            className={isDuplicate ? "is-invalid" : ""}
                          />
                        </div>
                        {index > 1 && (
                          <button
                            className="btn btn-outline-danger col-md-1 mt-2"
                            onClick={() => deleteLocationWording(index)}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                <button
                  className="btn btn-primary"
                  onClick={addMoreLocationWording}
                >
                  Add More
                </button>
              </ConfirmationDlg>
            </CardHeader>
            <CardBody>
              <ul className="nav nav-tabs pt-3 ps-4 pe-4">
                {locationWording.map((item, index) => {
                  return (
                    <li className="nav-item me-1" key={index}>
                      <a
                        href={"#tab" + index}
                        className={
                          index == selectedIndex
                            ? "nav-link active"
                            : "nav-link"
                        }
                        data-bs-toggle="tab"
                        onClick={() => setSelectedIndex(index)}
                      >
                        {item} Settings
                      </a>
                    </li>
                  );
                })}
              </ul>
              <div className="tab-content pt-3">
                <div className={"tab-pane fade show active"}>
                  <LocationTab
                    selectedIndex={selectedIndex}
                    onGrouped={onGrouped}
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
export default LocationConfiguration;
